import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import marketplaceOperatorService, { ModerationListing } from '../../../services/marketplaceOperatorService';
import { extractErrorMessage } from '../../../services/marketplaceService';
import { formatEuro } from '../../../pages/marketplaceVisibility';
import { ListingThumbnail } from '../Buyer/ListingCard';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box>
        <Typography variant="caption" sx={{ color: 'var(--oa-text-2)' }}>{label}</Typography>
        <Box>{typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}</Box>
    </Box>
);

/** Vorschau eines Angebots aus Sicht des Moderators: Darstellung, Preise, Kursstruktur, Gebühr. */
const ListingPreviewDialog: React.FC<{ listingId: number | null; onClose: () => void }> = ({ listingId, onClose }) => {
    const translate = useTranslate();
    const [listing, setListing] = useState<ModerationListing | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setListing(null);
        setError(null);
        if (listingId === null) return;
        marketplaceOperatorService
            .getModerationDetail(listingId)
            .then(setListing)
            .catch((err) => setError(extractErrorMessage(err, translate('marketplaceOperator.common.loadError'))));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingId]);

    const price = (value: string | null, enabled: boolean, currency: string) =>
        enabled ? (Number(value ?? 0) > 0 ? formatEuro(value, currency) : translate('marketplaceOperator.moderation.free')) : null;

    return (
        <Dialog open={listingId !== null} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="preview-title">
            <DialogTitle id="preview-title">{translate('marketplaceOperator.moderation.previewTitle')}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error">{error}</Alert>}
                {!listing && !error && <Skeleton variant="rectangular" height={260} />}
                {listing && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="listing-preview">
                        <ListingThumbnail src={listing.thumbnailUrl} height={160} />
                        <Typography variant="h5" component="h2">{listing.title}</Typography>
                        {listing.description && <Typography variant="body1">{listing.description}</Typography>}

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '1fr 1fr' }, gap: 2 }}>
                            <Field label={translate('marketplaceOperator.moderation.fields.seller')}>
                                {listing.sellerName ?? translate('marketplaceOperator.common.companyFallback', { id: listing.companyId })}
                            </Field>
                            <Field label={translate('marketplaceOperator.moderation.fields.category')}>{listing.category ?? '–'}</Field>
                            {listing.isBundle ? (
                                <Field label={translate('marketplaceOperator.moderation.fields.courses', { count: listing.courseCount ?? listing.courses?.length ?? 0 })}>
                                    <Box component="ol" sx={{ m: 0, pl: 3 }} data-testid="preview-bundle-courses">
                                        {(listing.courses ?? []).map((c) => <li key={c.id}><Typography variant="body2">{c.title}</Typography></li>)}
                                    </Box>
                                </Field>
                            ) : (
                                <Field label={translate('marketplaceOperator.moderation.fields.course')}>{listing.course?.title ?? '–'}</Field>
                            )}
                            <Field label={translate('marketplaceOperator.moderation.fields.fee')}>{`${listing.platformFeePercent} %`}</Field>
                        </Box>

                        <Field label={translate('marketplaceOperator.moderation.fields.prices')}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                {price(listing.priceReference, listing.allowReference, listing.currency) && (
                                    <Chip size="small" variant="outlined" label={`${translate('marketplaceOperator.moderation.fields.reference')}: ${price(listing.priceReference, listing.allowReference, listing.currency)}`} />
                                )}
                                {price(listing.priceCopy, listing.allowCopy, listing.currency) && (
                                    <Chip size="small" variant="outlined" label={`${translate('marketplaceOperator.moderation.fields.copy')}: ${price(listing.priceCopy, listing.allowCopy, listing.currency)}`} />
                                )}
                                {price(listing.priceSeat, listing.allowSeatCopy, listing.currency) && (
                                    <Chip size="small" variant="outlined" label={`${translate('marketplaceOperator.moderation.fields.seat')}: ${price(listing.priceSeat, listing.allowSeatCopy, listing.currency)}`} />
                                )}
                            </Box>
                        </Field>

                        {!!listing.highlights?.length && (
                            <Field label={translate('marketplaceOperator.moderation.fields.highlights')}>
                                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                    {listing.highlights.map((h, i) => <li key={i}><Typography variant="body2">{h}</Typography></li>)}
                                </Box>
                            </Field>
                        )}
                        {!!listing.tags?.length && (
                            <Field label={translate('marketplaceOperator.moderation.fields.tags')}>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                    {listing.tags.map((t) => <Chip key={t} size="small" label={t} />)}
                                </Box>
                            </Field>
                        )}
                        {listing.previewVideoUrl && (
                            <Field label={translate('marketplaceOperator.moderation.fields.video')}>
                                <Button component="a" href={listing.previewVideoUrl} target="_blank" rel="noopener noreferrer" size="small">
                                    {listing.previewVideoUrl}
                                </Button>
                            </Field>
                        )}
                        {listing.isBundle && (listing.courses ?? []).map((c) => !!c.lessons?.length && (
                            <Field key={c.id} label={`${c.title} — ${translate('marketplaceOperator.moderation.fields.lessons', { count: c.lessonCount ?? c.lessons?.length ?? 0 })}`}>
                                <Box component="ol" sx={{ m: 0, pl: 3 }}>
                                    {c.lessons!.map((l) => <li key={l.id}><Typography variant="body2">{l.title}</Typography></li>)}
                                </Box>
                            </Field>
                        ))}
                        {!listing.isBundle && !!listing.course?.lessons?.length && (
                            <Field label={translate('marketplaceOperator.moderation.fields.lessons', { count: listing.course.lessonCount })}>
                                <Box component="ol" sx={{ m: 0, pl: 3 }}>
                                    {listing.course.lessons.map((l) => <li key={l.id}><Typography variant="body2">{l.title}</Typography></li>)}
                                </Box>
                            </Field>
                        )}
                        {listing.rejectionReason && (
                            <Alert severity="info">
                                {translate('marketplaceOperator.moderation.fields.rejectionReason')}: {listing.rejectionReason}
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>{translate('marketplaceOperator.common.cancel')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ListingPreviewDialog;
